// ─── SCHERMATA INIZIO GIOCO (Gioca.cshtml) ─────────────────────
// Il bottone "GIOCA ORA" è il submit reale del form verso l'handler "Iwin"
// (che decide lato server l'esito e reindirizza a /vinto/{chiave} o /perso).
// OnPostIwin richiede però che l'azione "GIOCO" sia già stata salvata da
// OnPostStart, quindi il primo click viene intercettato per attendere quella
// fetch prima di lasciar proseguire la submit vera e propria.
export function initStartScreen() {
    const form = document.getElementById('gioca-form');
    const btn = document.getElementById('start-btn');
    if (!form || !btn) return;

    let started = false;

    form.addEventListener('submit', async (e) => {
        if (started) return;
        e.preventDefault();
        started = true;
        btn.disabled = true;

        const token = form.querySelector('input[name="__RequestVerificationToken"]')?.value;
        try {
            await fetch('/gioca?handler=Start', {
                method: 'POST',
                headers: { 'RequestVerificationToken': token }
            });
        } catch {
            // Se la fetch fallisce, OnPostIwin reindirizza comunque a "/" non trovando
            // l'azione GIOCO: nessuna gestione aggiuntiva necessaria lato FE.
        }

        form.requestSubmit(btn);
    });
}

// ─── SCENA FANTASMA (Vinto.cshtml / Perso.cshtml) ──────────────
// L'esito è già deciso lato server semplicemente trovandosi su questa pagina:
// il fantasma ondeggia sul posto, fa un giro completo sulle sei finestre
// illuminate del castello e si posa su quella target (centrale = vinto,
// laterale = perso); poi la scena sfuma e compare il contenuto reale (già
// presente nel markup, nascosto in #esito-content), senza alcun click.
//
// La traiettoria è costruita qui e non in CSS: servirebbe un @keyframes diverso
// per ogni combinazione di finestra target e breakpoint, con le coordinate
// duplicate rispetto a quelle qui sotto, e la rotazione non potrebbe comunque
// seguire la tangente del percorso. Con la Web Animations API campioniamo
// invece una spline che passa per le finestre reali, e da quella ricaviamo
// posizione, inclinazione e scala di ogni fotogramma.

// Centro delle sei finestre illuminate, in % del box dell'IMMAGINE di sfondo
// (castello.jpg 1943x1061 su desktop, castello-mobile.jpg 1125x1554 sotto i
// 768px: stesso breakpoint "md" dello swap d-md-none/d-md-inline). Misurate
// sulle immagini reali: per ricalibrare basta ritoccare questi numeri.
const GHOST_WINDOWS = {
    desktop: {
        left:       { x: 39.7, y: 70.0 },
        upperLeft:  { x: 40.4, y: 50.0 },
        tower:      { x: 50.7, y: 37.5 },
        upperRight: { x: 60.6, y: 50.0 },
        right:      { x: 62.0, y: 70.0 },
        center:     { x: 50.7, y: 70.0 }
    },
    mobile: {
        left:       { x: 22.9, y: 70.5 },
        upperLeft:  { x: 27.7, y: 49.5 },
        tower:      { x: 50.7, y: 39.9 },
        upperRight: { x: 73.3, y: 49.5 },
        right:      { x: 76.2, y: 70.5 },
        center:     { x: 49.8, y: 70.5 }
    }
};

// Le sei finestre formano un anello attorno alla facciata: si risale il fianco
// sinistro, si scavalca la torre, si ridiscende a destra e si rientra lungo il
// pianterreno. L'anello viene ruotato in modo che la finestra di atterraggio sia
// sempre l'ultima tappa, così il verso di percorrenza non si inverte mai: senza
// inversioni la curva non forma cuspidi e il volo resta continuo fino alla posa.
const GHOST_RING = ['left', 'upperLeft', 'tower', 'upperRight', 'right', 'center'];

// Baricentro dei pixel opachi di fantasma.png (424x278), in % del suo box: il
// disegno non è centrato, la testa sta in alto a sinistra e la scia si allunga
// in basso a destra. Senza questa correzione, posare il centro del box sul
// centro della finestra fa sembrare il fantasma spostato verso l'esterno.
const GHOST_ANCHOR = { x: 40.8, y: 56.2 };

// Quota a cui comincia il fantasma DISEGNATO dentro l'immagine dell'esito, in %
// dell'altezza dell'immagine: nel "perso" sta sul prato, nel "vinto" è affacciato
// alla finestra centrale, quindi più in alto. Misurata sui file reali; desktop e
// mobile coincidono, perciò basta un numero per esito. Serve a inquadrare a fine
// scena il fantasma insieme alla scritta vinto/perso.
const GHOST_IN_HERO = { win: 55.0, lose: 63.3 };

const GHOST_SWAY_MS = 1000;      // ondeggio iniziale, fermo al centro della scena
const GHOST_FLIGHT_MS = 7000;    // giro sulle finestre + discesa finale
const GHOST_FRAME_MS = 25;       // passo fra due keyframe, sia dell'ondeggio sia del volo
const GHOST_TANGENT_EPS = 0.006; // finestra (in frazione di arco) per derivare la direzione
const GHOST_FADE_MS = 600;       // deve combaciare con la transition di .scene-fade-out
const GHOST_BANK_DEG = 13;       // inclinazione massima in virata
const GHOST_LAND_SCALE = 0.85;   // scala con cui il fantasma si posa sulla finestra
const GHOST_STEPS_PER_LEG = 24;  // campioni per tratto della polilinea di servizio

// Posizione del punto d'ingresso nell'anello, come frazione del raggio e del
// passo angolare fra due finestre. Ricavati minimizzando la virata più stretta
// dell'intero percorso: con 0.65/0.4 nessuna curva supera i 99°, che è il minimo
// consentito dalla disposizione stessa delle finestre.
const GHOST_ENTRY_RADIUS = 0.65;
const GHOST_ENTRY_STEP = 0.4;

const clamp = (min, v, max) => Math.min(max, Math.max(min, v));
const smoothstep = (a, b, v) => { const t = clamp(0, (v - a) / (b - a), 1); return t * t * (3 - 2 * t); };

// L'ordine delle funzioni conta: scale e rotate agiscono sul centro del
// fantasma, la translate in px lo sposta sugli assi non ruotati del contenitore
// e la translate in % lo ricentra sul punto di ancoraggio CSS (left/top 50%).
const ghostTransform = (x, y, deg, scale) =>
    `translate(-50%, -50%) translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) rotate(${deg.toFixed(2)}deg) scale(${scale.toFixed(3)})`;

// Catmull-Rom centripeta (alpha = .5): passa esattamente per tutti i punti e, a
// differenza della versione uniforme, non produce cappi né sovraelongazioni
// quando i tratti hanno lunghezze molto diverse fra loro.
const crKnot = (prev, a, b) => prev + Math.max(1e-3, Math.sqrt(Math.hypot(b.x - a.x, b.y - a.y)));

function crPoint(p0, p1, p2, p3, t) {
    const t0 = 0;
    const t1 = crKnot(t0, p0, p1);
    const t2 = crKnot(t1, p1, p2);
    const t3 = crKnot(t2, p2, p3);
    const tt = t1 + (t2 - t1) * t;
    const mix = (a, b, ta, tb) => {
        const k = (tb - tt) / (tb - ta);
        return { x: k * a.x + (1 - k) * b.x, y: k * a.y + (1 - k) * b.y };
    };
    const a1 = mix(p0, p1, t0, t1);
    const a2 = mix(p1, p2, t1, t2);
    const a3 = mix(p2, p3, t2, t3);
    return mix(mix(a1, a2, t0, t2), mix(a2, a3, t1, t3), t1, t2);
}

// Campiona la spline fitto abbastanza perché l'interpolazione lineare fra un
// keyframe e il successivo non lasci spigoli visibili sulle finestre.
function sampleSpline(points, stepsPerLeg) {
    const last = points.length - 1;
    // punti fantasma agli estremi: la curva parte e finisce diritta, senza
    // ricciolo alla partenza né uncino sull'atterraggio
    const ext = [
        { x: 2 * points[0].x - points[1].x, y: 2 * points[0].y - points[1].y },
        ...points,
        { x: 2 * points[last].x - points[last - 1].x, y: 2 * points[last].y - points[last - 1].y }
    ];
    const out = [];
    for (let i = 1; i < ext.length - 2; i++) {
        for (let s = 0; s < stepsPerLeg; s++) {
            out.push(crPoint(ext[i - 1], ext[i], ext[i + 1], ext[i + 2], s / stepsPerLeg));
        }
    }
    out.push(points[last]);
    return out;
}

// Dal centro della scena il fantasma non può puntare dritto alla prima finestra:
// arriverebbe contromano rispetto al giro e la curva si chiuderebbe in un
// tornante. Lo facciamo quindi uscire a spirale passando per un punto interno
// all'anello, arretrato di una tappa rispetto alla finestra d'ingresso.
function ringEntry(ring) {
    const cx = ring.reduce((s, p) => s + p.x, 0) / ring.length;
    const cy = ring.reduce((s, p) => s + p.y, 0) / ring.length;
    const first = ring[0];
    const angle = Math.atan2(first.y - cy, first.x - cx) - GHOST_ENTRY_STEP * ((2 * Math.PI) / ring.length);
    const radius = Math.hypot(first.x - cx, first.y - cy) * GHOST_ENTRY_RADIUS;
    return { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius };
}

export function initGhostScene() {
    const scene = document.querySelector('.ghost-scene');
    const content = document.getElementById('esito-content');
    if (!scene || !content) return;

    const ghost = scene.querySelector('.ghost-fly');
    const stage = scene.querySelector('.scene-castello') || scene;
    if (!ghost) return;

    const targetKey = scene.dataset.outcome === 'win'
        ? 'center'
        : (Math.random() < 0.5 ? 'left' : 'right');

    let revealed = false;
    let anim = null;
    let safetyId = 0;
    let resizeId = 0;
    let lastWidth = 0;
    let attempts = 0;

    const reveal = () => {
        if (revealed) return;
        revealed = true;
        clearTimeout(safetyId);
        clearTimeout(resizeId);
        window.removeEventListener('resize', onResize);
        scene.classList.add('scene-fade-out');
        setTimeout(() => {
            scene.classList.add('d-none');
            content.classList.remove('d-none');
            requestAnimationFrame(() => {
                content.classList.add('fade-in');
                frameOutcome();
            });
        }, GHOST_FADE_MS);
    };

    // Inquadra una fascia verticale della pagina nello spazio libero sotto
    // l'header sticky: se ci sta la centra, se è più alta della viewport
    // privilegia il fondo, dove sta la scritta dell'esito.
    const scrollBandIntoView = (top, bottom) => {
        const header = document.querySelector('header.sticky-top');
        const hh = header ? header.offsetHeight : 0;
        const view = window.innerHeight;
        const free = view - hh;
        const height = bottom - top;
        const y = height <= free ? top - hh - (free - height) / 2 : bottom - view;
        const max = Math.max(0, document.documentElement.scrollHeight - view);
        window.scrollTo({
            top: clamp(0, y, max),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
    };

    // Scrollare sul solo titolo lasciava fuori dal bordo alto il fantasma
    // disegnato nell'immagine dell'esito. Inquadriamo invece tutta la fascia che
    // va dal fantasma alla scritta vinto/perso, così si vedono insieme.
    const frameOutcome = () => {
        const title = content.querySelector('#esito_title');
        const hero = title && title.parentElement;
        if (!hero) return;
        const bg = Array.from(hero.querySelectorAll('img')).find((el) => el !== title && el.offsetWidth > 0);
        if (!bg) return;
        const b = bg.getBoundingClientRect();
        const y = window.pageYOffset;
        const share = GHOST_IN_HERO[scene.dataset.outcome === 'win' ? 'win' : 'lose'];
        scrollBandIntoView(b.top + y + (share / 100) * b.height, title.getBoundingClientRect().bottom + y);
    };

    // Misura lo sfondo VISIBILE rispetto al contenitore, così le percentuali
    // prese sui JPG restano valide comunque sia impaginata l'immagine; ed è
    // l'immagine visibile a dire quale delle due mappe di coordinate usare.
    const measure = () => {
        const bg = Array.from(scene.querySelectorAll('.scene-bg')).find((el) => el.offsetWidth > 0);
        if (!bg) return null;
        const s = stage.getBoundingClientRect();
        const b = bg.getBoundingClientRect();
        if (b.width < 120 || b.height < 120) return null; // sfondo non ancora dimensionato
        const gw = ghost.offsetWidth;
        const gh = ghost.offsetHeight;
        // il baricentro va portato sul centro della finestra: il box del fantasma
        // si sposta quindi dello scarto fra centro box e baricentro, riscalato
        // come sarà al momento della posa
        const ax = ((50 - GHOST_ANCHOR.x) / 100) * gw * GHOST_LAND_SCALE;
        const ay = ((50 - GHOST_ANCHOR.y) / 100) * gh * GHOST_LAND_SCALE;
        return {
            width: s.width,
            ghostH: gh,
            map: GHOST_WINDOWS[bg.classList.contains('d-md-none') ? 'mobile' : 'desktop'],
            // da % dell'immagine a scostamento in px dal centro del contenitore
            toPx: (p) => ({
                x: (b.left - s.left) + (p.x / 100) * b.width - s.width / 2 + ax,
                y: (b.top - s.top) + (p.y / 100) * b.height - s.height / 2 + ay
            })
        };
    };

    const buildKeyframes = (geom) => {
        const i = GHOST_RING.indexOf(targetKey);
        const ring = GHOST_RING.slice(i + 1).concat(GHOST_RING.slice(0, i + 1))
            .map((key) => geom.toPx(geom.map[key]));

        // il volo parte dalla posa di riposo (centro scena = scostamento nullo).
        // Questa polilinea fitta non produce keyframe: serve da tabella per la
        // lunghezza d'arco, da cui poi si pesca per interpolazione.
        const dense = sampleSpline([{ x: 0, y: 0 }, ringEntry(ring), ...ring], GHOST_STEPS_PER_LEG);
        const arc = [0];
        for (let k = 1; k < dense.length; k++) {
            arc.push(arc[k - 1] + Math.hypot(dense[k].x - dense[k - 1].x, dense[k].y - dense[k - 1].y));
        }
        const total = arc[arc.length - 1] || 1;

        // posizione a una data frazione di lunghezza d'arco, per ricerca binaria
        // (senza stato: si può interrogare anche a ritroso, e serve per la
        // derivata qui sotto)
        const posAt = (frac) => {
            const d = clamp(0, frac, 1) * total;
            let lo = 1;
            let hi = arc.length - 1;
            while (lo < hi) {
                const mid = (lo + hi) >> 1;
                if (arc[mid] < d) lo = mid + 1; else hi = mid;
            }
            const a = dense[lo - 1];
            const b = dense[lo];
            const f = clamp(0, (d - arc[lo - 1]) / ((arc[lo] - arc[lo - 1]) || 1), 1);
            return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
        };

        const duration = GHOST_SWAY_MS + GHOST_FLIGHT_MS;
        const swayFrac = GHOST_SWAY_MS / duration;
        const out = [];

        // fase 1 - ondeggio sul posto. L'inviluppo sin^2 si annulla in u=1 con
        // derivata nulla: il fantasma arriva fermo al momento di partire, mentre
        // con un inviluppo sin semplice ci finiva dentro ancora in movimento e si
        // vedeva uno scatto nel passaggio al volo.
        const SWAY_STEPS = Math.max(12, Math.round(GHOST_SWAY_MS / GHOST_FRAME_MS));
        for (let k = 0; k <= SWAY_STEPS; k++) {
            const u = k / SWAY_STEPS;
            const env = Math.sin(Math.PI * u) ** 2;
            out.push({
                offset: swayFrac * u * 0.999,
                transform: ghostTransform(0, -9 * Math.sin(2 * Math.PI * 1.25 * u) * env, 6 * Math.sin(2 * Math.PI * 1.5 * u) * env, 1)
            });
        }

        // fase 2 - volo. I keyframe sono equidistanti NEL TEMPO e l'easing sta
        // nella posizione (easeInOutSine sulla lunghezza d'arco). Prima era il
        // contrario - campioni equidistanti nello spazio e offset "eased" - ed è
        // da lì che veniva lo scatto: fra due keyframe la velocità è costante,
        // quindi cambiava di colpo a ogni keyframe, e in partenza il salto era
        // del 140% (42 -> 102 px/s) perché il primo tratto durava 260 ms e il
        // secondo 110. Così invece la velocità sale e scende con continuità.
        const steps = Math.max(60, Math.round(GHOST_FLIGHT_MS / GHOST_FRAME_MS));
        const amp = Math.max(4, geom.ghostH * 0.085);
        for (let k = 0; k <= steps; k++) {
            const t = k / steps;
            const s = (1 - Math.cos(Math.PI * t)) / 2; // easeInOutSine
            const p = posAt(s);
            // la direzione si deriva dalle posizioni interpolate, non dagli
            // indici della polilinea: letta dagli indici, l'inclinazione
            // scattava di colpo ogni volta che il campione di riferimento
            // cambiava - ed è un salto tanto più visibile quanto più il
            // fantasma è lento, cioè proprio in partenza e in atterraggio
            const back = posAt(s - GHOST_TANGENT_EPS);
            const fwd = posAt(s + GHOST_TANGENT_EPS);
            const dx = fwd.x - back.x;
            const dy = fwd.y - back.y;
            const len = Math.hypot(dx, dy) || 1;
            const land = smoothstep(0.8, 1, t);
            // l'ondeggio entra solo nella posizione: se lo lasciassimo anche
            // nella direzione usata per l'inclinazione, il fantasma tremerebbe a
            // ogni oscillazione invece di coricarsi dolcemente nelle curve
            const w = Math.sin(2 * Math.PI * 3.5 * t) * Math.sin(Math.PI * t) ** 2 * amp * (1 - land);
            const deg = (GHOST_BANK_DEG * (dx / len) + 2.5 * Math.sin(2 * Math.PI * 2 * t))
                * smoothstep(0, 0.08, t) * (1 - land);
            const breath = 1 + 0.04 * Math.sin(2 * Math.PI * 1.5 * t);
            out.push({
                offset: Math.min(1, swayFrac + (1 - swayFrac) * t),
                transform: ghostTransform(
                    p.x + (-dy / len) * w,
                    p.y + (dx / len) * w,
                    deg,
                    breath + (GHOST_LAND_SCALE - breath) * land
                )
            });
        }
        out[out.length - 1].offset = 1;
        return { keyframes: out, duration };
    };

    const run = (geom, startAt) => {
        const { keyframes, duration } = buildKeyframes(geom);
        lastWidth = geom.width;
        anim = ghost.animate(keyframes, { duration, easing: 'linear', fill: 'forwards' });
        anim.onfinish = reveal;
        if (startAt) anim.currentTime = Math.min(startAt, duration);
        clearTimeout(safetyId);
        // safety-net legata alla durata reale: se l'animazione non parte o non
        // termina, l'esito viene mostrato comunque
        safetyId = setTimeout(reveal, duration - (startAt || 0) + 1200);
    };

    // Su resize/rotazione la geometria cambia, e sotto i 768px cambia anche
    // l'immagine e quindi la mappa delle finestre: si ricostruisce la stessa
    // rotta sulle nuove misure ripartendo dall'istante corrente.
    function onResize() {
        if (revealed || !anim) return;
        clearTimeout(resizeId);
        resizeId = setTimeout(() => {
            if (revealed || !anim) return;
            const geom = measure();
            if (!geom || Math.abs(geom.width - lastWidth) < 40) return;
            const at = Number(anim.currentTime) || 0;
            anim.onfinish = null;
            anim.cancel();
            run(geom, at);
        }, 180);
    }

    const play = () => {
        if (revealed) return;
        const geom = measure();
        if (!geom) { // sfondo non ancora dimensionato: riprova, poi si arrende
            if (attempts++ < 60) requestAnimationFrame(play);
            else reveal();
            return;
        }
        run(geom, 0);
        window.addEventListener('resize', onResize);
    };

    // Accessibilità e browser senza Web Animations API: niente volo, il fantasma
    // è già posato sulla finestra e la scena sfuma dopo una breve pausa.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof ghost.animate !== 'function') {
        const geom = measure();
        if (geom) {
            const p = geom.toPx(geom.map[targetKey]);
            ghost.style.transform = ghostTransform(p.x, p.y, 0, GHOST_LAND_SCALE);
        }
        safetyId = setTimeout(reveal, 1200);
        return;
    }

    // Lo sfondo è il fotogramma zero dell'animazione: initGhostScene() parte su
    // DOMContentLoaded, quindi senza questa attesa il fantasma comincerebbe a
    // svolazzare mentre il castello non è ancora dipinto.
    const bg = Array.from(scene.querySelectorAll('.scene-bg')).find((el) => el.offsetWidth > 0);
    if (!bg || bg.complete) {
        play();
    } else {
        let started = false;
        const go = () => { if (!started) { started = true; play(); } };
        bg.addEventListener('load', go, { once: true });
        bg.addEventListener('error', go, { once: true });
        setTimeout(go, 2000); // se l'immagine non arriva, si parte lo stesso
    }
}
